import { cloneDeep } from "lodash";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgDialog, IgTypography } from "@intelligems/ig-theme";
import { groupRounder, RounderObject } from "@intelligems/ig-utils";
import { Grid } from "@mui/material";

import {
  getTempVariations,
  isZero,
} from "../../api/formatters/from-dto/experiences/variations";
import { usePatchVariations } from "../../api/variations/patch-variation";
import { GroupSlider } from "../../features/experienceTests/components/variationSetter/Utils/GroupSlider";
import { selectedExperience } from "../../store/slices/experiences";
import { setDialog } from "../../store/slices/global";
import { selectedVariations } from "../../store/slices/variations";
import { IAppState } from "../../store/types/store";

interface EditTestGroupsDialogProps {
  editTestGroupsDialog: boolean;
}

export const EditTestGroupsDialog = ({
  editTestGroupsDialog,
}: EditTestGroupsDialogProps) => {
  const experience = useSelector(selectedExperience);
  const variations = useSelector(selectedVariations);
  const dispatch = useDispatch();
  const patchVariations = usePatchVariations(
    experience.category,
    experience.id
  );

  const [newVariationState, setNewVariationState] = useImmer(variations);
  const sliderWidth = useSelector((state: IAppState) =>
    state.appSettings.useWideApp ? 2000 : 1000
  );

  const handleClose = () => {
    dispatch(setDialog({ dialog: "editTestGroups", open: false }));
  };
  const onSubmit = () => {
    patchVariations.mutate({
      variations: newVariationState,
    });
  };

  // update new state when new experiment is navigated to
  useEffect(() => {
    setNewVariationState(variations);
  }, [experience.id]);

  const customSlider = (
    movementData: any,
    groupIndex: number,
    testGroups: RounderObject[]
  ) => {
    const { deltaX } = movementData;
    const tempTestGroups = getTempVariations(
      newVariationState,
      groupIndex,
      deltaX,
      sliderWidth
    );

    if (isZero(testGroups[groupIndex - 1], deltaX) && deltaX < 0) {
      return;
    }

    if (isZero(testGroups[groupIndex], deltaX) && deltaX > 0) {
      return;
    }

    setNewVariationState((prevState) => {
      let copyState = cloneDeep(prevState);
      copyState = groupRounder(tempTestGroups);
      return copyState;
    });
  };

  useEffect(() => {
    if (patchVariations.isSuccess) {
      dispatch(setDialog({ dialog: "editTestGroups", open: false }));
    }
  }, [patchVariations.isSuccess]);

  return (
    <IgDialog
      fullWidth={true}
      handleClose={handleClose}
      maxWidth={"lg"}
      dialogHeight={null}
      showDialog={editTestGroupsDialog}
      showCloseIcon={true}
      title={"Edit Test Group Traffic Split"}
      yesButtonText={"Save Changes"}
      loading={patchVariations.isPending}
      noButtonText={"Discard Changes"}
      handleNoClick={handleClose}
      handleYesClick={() => {
        void onSubmit();
      }}
      subtitle={
        <IgTypography>
          Please note that once a test is started, we generally recommend
          keeping the <br /> relative proportions of test groups consistent to
          avoid skewing results. <br />
          If you would like to stop sending traffic to a group, set the group's
          traffic <br />
          to 0%, but we recommend allocating that group's traffic evenly
          <br /> among the remaining groups (for example changing a 33/33/33
          split to 50/50/0)
        </IgTypography>
      }
      rawContent={
        <Grid item>
          <GroupSlider
            showPercent={true}
            onSliderBarSlide={customSlider}
            objects={newVariationState}
          />
        </Grid>
      }
    />
  );
};
